import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SiteFooter from '../components/SiteFooter';
import { Mic, MicOff, ArrowLeft, X, Sparkles, Paperclip, FileText } from 'lucide-react';
import TutorialLaunchButton from '../components/tutorial/TutorialLaunchButton';
import ThemeToggle from '../components/ThemeToggle';
import { useTutorial } from '../components/tutorial/TutorialContext';
import SageParticleOrb from '../components/SageParticleOrb';
import { AttachDocsSheet } from '../components/ReferenceDocsPanel';
// Note: SAGE's text response is no longer rendered in the UI — voice-first
// mode. See the "voice-first coaching" comment below for the layout rationale.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const LiveCoaching = () => {
  const navigate = useNavigate();
  // If the onboarding tour is currently on the /live-coaching step, we
  // must NOT auto-start a real coaching session or its opening greeting —
  // otherwise the tour's voice-over and SAGE's greeting play over each
  // other. The init effect below gates on this flag; when the tour leaves
  // this page (unmounts LiveCoaching) or ends, subsequent visits behave
  // normally.
  const { isActive: tutorialActive } = useTutorial();
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('Initializing...');
  const [orbPulse, setOrbPulse] = useState(1);
  const [sessionId, setSessionId] = useState(null);
  // Reference documents attached to THIS session. Populated when the user
  // opens the paperclip and picks/uploads. IDs only — the AttachDocsSheet
  // owns the summary metadata it renders inside itself.
  const [attachedDocs, setAttachedDocs] = useState([]);      // full summaries for chip render
  const [attachedDocIds, setAttachedDocIds] = useState([]);  // just the IDs
  const [attachSheetOpen, setAttachSheetOpen] = useState(false);

  // Whenever the attached-IDs list changes, refresh the summaries used to
  // render chips. Kept small: just title + kind so we can show a chip.
  useEffect(() => {
    if (!sessionId || attachedDocIds.length === 0) {
      setAttachedDocs([]);
      return;
    }
    const token = localStorage.getItem('sage_token');
    axios
      .get(`${API}/coaching/session/${sessionId}/reference-documents`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setAttachedDocs(res.data.attached || []))
      .catch(() => {
        // Non-fatal: chips just won't render.
      });
  }, [attachedDocIds, sessionId]);

  const detachDoc = async (docId) => {
    if (!sessionId) return;
    const token = localStorage.getItem('sage_token');
    try {
      await axios.post(
        `${API}/coaching/session/${sessionId}/detach`,
        { doc_ids: [docId] },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAttachedDocIds((prev) => prev.filter((id) => id !== docId));
    } catch {
      toast.error('Could not detach document');
    }
  };
  const [useHttpFallback, setUseHttpFallback] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [showEndSummary, setShowEndSummary] = useState(false);
  const [sessionSummary, setSessionSummary] = useState(null);
  const [isEnding, setIsEnding] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [displayedText, setDisplayedText] = useState('');  // Text to show after audio
  // True while SAGE's opening greeting is playing — used to hide the mic button
  // and "press and hold to speak" hint until SAGE has finished her greeting.
  const [isGreeting, setIsGreeting] = useState(false);
  
  const speechSynthesisRef = useRef(null);
  const recognitionRef = useRef(null);
  const audioElementRef = useRef(null);  // Single audio element for ElevenLabs
  // Web Audio graph for SAGE's TTS output — used to sample the voice
  // waveform in real-time so the particle orb visibly reacts to her
  // syllables (see SageParticleOrb `audioAnalyser` prop). Separate from
  // the mic-input analyser below so both can coexist.
  const sageAudioCtxRef = useRef(null);
  const sageAnalyserRef = useRef(null);
  const sageMediaSourceRef = useRef(null);
  
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const timerRef = useRef(null);
  // Guards initializeConnection() against React StrictMode's double-invoke
  // of mount effects (dev only) and accidental remounts.
  const hasInitializedRef = useRef(false);
  // Persists finalized speech segments across pauses and recognition restarts.
  // Web Speech API drops finalized chunks from event.results once resultIndex
  // advances (and clears them entirely on .start() after onend), so without
  // this buffer any text spoken before a pause is lost.
  const finalTranscriptRef = useRef('');
  // Mirrors isRecording for use inside long-lived browser API callbacks
  // (onend, etc.) that were registered once on mount and otherwise close over
  // a stale `false` value. Without this, recognition cannot auto-restart on
  // silence and the user's pause-then-resume speech is dropped.
  const isRecordingRef = useRef(false);

  // Session timer effect
  useEffect(() => {
    if (isConnected && !sessionStartTime) {
      setSessionStartTime(new Date());
    }
    
    if (isConnected && sessionStartTime) {
      timerRef.current = setInterval(() => {
        setElapsedTime(Math.floor((new Date() - sessionStartTime) / 1000));
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isConnected, sessionStartTime]);

  // Format elapsed time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format current date
  const formatDate = () => {
    const now = new Date();
    const options = { weekday: 'long', month: 'short', day: 'numeric' };
    return now.toLocaleDateString('en-US', options);
  };

  useEffect(() => {
    if (hasInitializedRef.current) return;
    // Suppress init while the onboarding tour is showing this page — the
    // tour has its own voice-over and shouldn't compete with SAGE's live
    // greeting. Once the tour advances (unmounts this page) or ends, the
    // effect re-runs and normal init proceeds.
    if (tutorialActive) {
      setStatus('SAGE is ready');
      return;
    }
    hasInitializedRef.current = true;
    initializeConnection();
    
    // Initialize speech synthesis
    if ('speechSynthesis' in window) {
      speechSynthesisRef.current = window.speechSynthesis;
    }
    
    // Initialize speech recognition
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
      
      recognitionRef.current.onresult = (event) => {
        // Accumulate finalized chunks into a persistent buffer so that text
        // captured before a pause (or before an automatic restart on silence)
        // is never lost. Only interim (in-progress) segments are recomputed
        // each event.
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            const chunk = result[0].transcript;
            // Ensure single spacing between accumulated final chunks.
            finalTranscriptRef.current =
              (finalTranscriptRef.current + ' ' + chunk).replace(/\s+/g, ' ').trim();
          } else {
            interim += result[0].transcript;
          }
        }
        const combined = (finalTranscriptRef.current + ' ' + interim)
          .replace(/\s+/g, ' ')
          .trim();
        setTranscribedText(combined);
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'not-allowed') {
          toast.error('Microphone permission denied. Please allow microphone access.');
        } else if (event.error === 'network') {
          toast.error('Network error during speech recognition. Please try again.');
        } else if (event.error === 'no-speech') {
          // Don't show error for no-speech, it's handled in stopRecording
        } else if (event.error === 'aborted') {
          // User or system aborted, don't show error
        } else {
          toast.error('Speech recognition encountered an issue. Try using text input.');
        }
      };
      
      // Handle when speech recognition ends unexpectedly (e.g. browser
      // auto-stops on detected silence). If the user is still holding the
      // mic, restart immediately — finalTranscriptRef preserves prior text.
      recognitionRef.current.onend = () => {
        if (isRecordingRef.current && recognitionRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            // SpeechRecognition.start() throws when already running or stopped
            // intentionally; this is an expected, non-error condition.
            if (process.env.NODE_ENV !== 'production') {
              console.debug('SpeechRecognition.start ignored:', e?.message);
            }
          }
        }
      };
    } else {
      console.warn('Speech recognition not supported');
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      stopAllAudio();
      stopSpeaking();
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      // Clean up audio element
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        if (audioElementRef.current.src) {
          URL.revokeObjectURL(audioElementRef.current.src);
        }
        audioElementRef.current = null;
      }
    };
    // Mount/unmount-only effect: sets up the connection + browser APIs once,
    // and tears them down on unmount. Including all closures referenced inside
    // (initializeConnection, stopAllAudio, etc.) would re-run setup on every
    // render and break the live session. `tutorialActive` IS included as a
    // dep so that if the tour ends while this page is still mounted, the
    // effect re-runs and normal init proceeds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorialActive]);

  const stopSpeaking = () => {
    if (speechSynthesisRef.current) {
      speechSynthesisRef.current.cancel();
    }
    // Also stop ElevenLabs audio if playing
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
  };

  const playElevenLabsAudio = (base64Audio, responseText = '') => {
    return new Promise((resolve) => {
      try {
        // Stop any currently playing audio first
        if (audioElementRef.current) {
          audioElementRef.current.pause();
          if (audioElementRef.current.src) {
            URL.revokeObjectURL(audioElementRef.current.src);
          }
        }
        
        // Clear displayed text while audio plays
        setDisplayedText('');
        
        // Convert base64 to audio blob
        const audioBytes = atob(base64Audio);
        const audioArray = new Uint8Array(audioBytes.length);
        for (let i = 0; i < audioBytes.length; i++) {
          audioArray[i] = audioBytes.charCodeAt(i);
        }
        const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Create or reuse audio element
        if (!audioElementRef.current) {
          audioElementRef.current = new Audio();
          // Mobile-safety attributes. iOS refuses inline playback without
          // `playsInline`; Android + iOS both benefit from `preload=auto`
          // so the first play() doesn't race with buffering.
          audioElementRef.current.playsInline = true;
          audioElementRef.current.setAttribute('playsinline', '');
          audioElementRef.current.setAttribute('webkit-playsinline', '');
          audioElementRef.current.preload = 'auto';
          audioElementRef.current.crossOrigin = 'anonymous';
        }
        
        const audio = audioElementRef.current;
        
        // Reset playback rate to ensure consistent speed
        audio.playbackRate = 1.0;
        audio.preservesPitch = true;
        
        // MOBILE AUDIO FIX: detect mobile up front. On mobile browsers,
        // wiring the audio element through a Web Audio AudioContext
        // silences playback whenever the context is suspended (which is
        // the default state on iOS/Android until a fresh user gesture).
        // The analyser is a nice-to-have for the particle orb; audio is
        // the WHOLE product. So we skip the analyser on mobile — the orb
        // gracefully falls back to synthesized pulse animation.
        const isMobile = typeof navigator !== 'undefined' && (
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
          (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.platform))  // iPadOS 13+ desktop UA
        );
        
        // Set up event handlers
        const handlePlay = () => {
          setIsSpeaking(true);
          setOrbPulse(1.3);
          if (isMobile) {
            // Skip the Web Audio graph entirely on mobile. See above.
            return;
          }
          // Lazily connect SAGE's audio element into a Web Audio graph
          // so the particle orb can read her voice amplitude. Must
          // happen after a user gesture (which starting a session
          // counts as). MediaElementAudioSource can only be created
          // ONCE per element — after that we reuse the existing graph.
          try {
            if (!sageMediaSourceRef.current && audioElementRef.current) {
              const Ctx = window.AudioContext || window.webkitAudioContext;
              if (Ctx) {
                const ctxAudio = sageAudioCtxRef.current || new Ctx();
                sageAudioCtxRef.current = ctxAudio;
                if (ctxAudio.state === 'suspended') {
                  ctxAudio.resume().catch(() => {});
                }
                const src = ctxAudio.createMediaElementSource(audioElementRef.current);
                const analyser = ctxAudio.createAnalyser();
                analyser.fftSize = 256;              // 128 bins — enough for a smooth amplitude read
                analyser.smoothingTimeConstant = 0.75;
                src.connect(analyser);
                analyser.connect(ctxAudio.destination); // keep audio audible
                sageMediaSourceRef.current = src;
                sageAnalyserRef.current = analyser;
              }
            } else if (sageAudioCtxRef.current?.state === 'suspended') {
              // Reuse existing graph, just resume the context.
              sageAudioCtxRef.current.resume().catch(() => {});
            }
          } catch (e) {
            // Non-fatal — orb will fall back to synthesized pulse.
            console.warn('SAGE audio analyser setup failed:', e);
          }
        };
        
        const handleEnded = () => {
          setIsSpeaking(false);
          setOrbPulse(1);
          setStatus('SAGE is ready');
          // Fade in the full text AFTER audio finishes
          if (responseText) {
            setDisplayedText(responseText);
          }
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        
        const handleError = (error) => {
          console.error('Audio playback error:', error);
          setIsSpeaking(false);
          setOrbPulse(1);
          // Show full text on error
          if (responseText) {
            setDisplayedText(responseText);
          }
          URL.revokeObjectURL(audioUrl);
          resolve();
        };
        
        // Remove old listeners and add new ones
        audio.onplay = handlePlay;
        audio.onended = handleEnded;
        audio.onerror = handleError;
        
        // Set source and play
        audio.src = audioUrl;
        audio.load();  // Explicitly load the new source
        
        audio.play().catch((error) => {
          console.error('Failed to play audio:', error);
          setIsSpeaking(false);
          // Show full text on error
          if (responseText) {
            setDisplayedText(responseText);
          }
          URL.revokeObjectURL(audioUrl);
          resolve();
        });
        
      } catch (error) {
        console.error('Failed to process audio:', error);
        setIsSpeaking(false);
        resolve();
      }
    });
  };

  const speakText = (text) => {
    return new Promise((resolve) => {
      if (!speechSynthesisRef.current) {
        toast.error('Speech synthesis not supported');
        resolve();
        return;
      }

      // Cancel any ongoing speech
      speechSynthesisRef.current.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Configure voice settings for a warm, conversational female coaching voice
      utterance.rate = 0.95; // Natural speaking pace
      utterance.pitch = 1.0; // Natural pitch for female voice
      utterance.volume = 1.0;
      
      // Select a suitable female voice
      const voices = speechSynthesisRef.current.getVoices();
      const preferredVoice = voices.find(voice => 
        voice.name.includes('Google US English') ||
        voice.name.includes('Samantha') ||
        voice.name.includes('Microsoft Zira') ||
        voice.name.includes('Victoria') ||
        (voice.lang.startsWith('en') && voice.name.toLowerCase().includes('female'))
      );
      
      // Fallback to any English female voice
      const femaleVoice = preferredVoice || voices.find(voice => 
        voice.lang.startsWith('en') && !voice.name.toLowerCase().includes('male')
      );
      
      if (femaleVoice) {
        utterance.voice = femaleVoice;
        console.log('Using voice:', femaleVoice.name);
      }

      utterance.onstart = () => {
        setIsSpeaking(true);
        setOrbPulse(1.3);
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setOrbPulse(1);
        setStatus('SAGE is ready');
        resolve();
      };

      utterance.onerror = (error) => {
        console.error('Speech synthesis error:', error);
        setIsSpeaking(false);
        setOrbPulse(1);
        resolve();
      };

      speechSynthesisRef.current.speak(utterance);
    });
  };

  const initializeConnection = async () => {
    // Try WebSocket first
    try {
      await connectWebSocket();
    } catch (error) {
      console.log('WebSocket failed, using HTTP fallback');
      await startHttpSession();
    }
  };

  const startHttpSession = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Detect user's IANA timezone (e.g., 'America/Chicago') so SAGE
      // can be day/time aware in its responses.
      let detectedTz = null;
      try {
        detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (tzErr) {
        // Old browsers / restricted contexts (e.g. some embedded WebViews) do
        // not implement Intl.DateTimeFormat. This is expected, non-fatal —
        // the backend falls back to UTC. Log only in dev for visibility.
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Timezone detection unavailable:', tzErr?.message);
        }
      }

      const response = await axios.post(
        `${API}/coaching/start${detectedTz ? `?timezone_name=${encodeURIComponent(detectedTz)}` : ''}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSessionId(response.data.session_id);
      setUseHttpFallback(true);
      setIsConnected(true);
      setStatus('SAGE is ready');
      toast.success('Connected to SAGE');

      // --- Fetch and play SAGE's opening greeting (day-aware, Seven Spiritual Laws) ---
      try {
        setIsGreeting(true);
        const greetingResp = await axios.post(
          `${API}/coaching/greeting?session_id=${response.data.session_id}${
            detectedTz ? `&timezone_name=${encodeURIComponent(detectedTz)}` : ''
          }`,
          {},
          { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
        );
        const greetingText = greetingResp.data.text;
        const greetingAudio = greetingResp.data.audio;
        if (greetingText) {
          setStatus('SAGE speaks');
          if (greetingResp.data.has_audio && greetingAudio) {
            await playElevenLabsAudio(greetingAudio, greetingText);
          } else {
            setDisplayedText(greetingText);
            await speakText(greetingText);
          }
        }
      } catch (greetingErr) {
        // Greeting is non-critical; log and continue
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Greeting fetch failed (non-fatal):', greetingErr?.message);
        }
      } finally {
        setIsGreeting(false);
      }
    } catch (error) {
      console.error('Failed to start HTTP session:', error);
      toast.error('Connection failed');
      setStatus('Connection failed');
    }
  };

  const stopAllAudio = () => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
    }
    // Also close SAGE's TTS-analysis context so we don't leak an audio
    // graph across sessions. MediaElementAudioSource can't be re-created
    // for the same element, so we drop the ref too — the next session
    // will build a fresh element + graph together.
    if (sageAudioCtxRef.current && sageAudioCtxRef.current.state !== 'closed') {
      sageAudioCtxRef.current.close().catch(() => {});
    }
    sageAudioCtxRef.current = null;
    sageAnalyserRef.current = null;
    sageMediaSourceRef.current = null;
  };

  const connectWebSocket = () => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('sage_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const ws = new WebSocket(`${WS_URL}/ws/coaching?token=${token}`);
      ws.binaryType = 'arraybuffer';
      
      const timeout = setTimeout(() => {
        ws.close();
        reject(new Error('WebSocket connection timeout'));
      }, 5000);

      ws.onopen = () => {
        clearTimeout(timeout);
        setIsConnected(true);
        setStatus('SAGE is ready');
        setUseHttpFallback(false);
        toast.success('Connected to SAGE via WebSocket');
        resolve();
      };

      ws.onmessage = async (event) => {
        // Handle text messages
        if (typeof event.data === 'string') {
          try {
            const message = JSON.parse(event.data);
            
            if (message.type === 'ready') {
              setStatus(message.message);
            } else if (message.type === 'status') {
              setStatus(message.message);
            } else if (message.type === 'error') {
              toast.error(message.message);
              setStatus('Error occurred');
            }
          } catch (error) {
            console.error('Failed to parse message:', error);
          }
        } 
        // Handle audio data
        else if (event.data instanceof ArrayBuffer) {
          await playAudioChunk(event.data);
        }
      };

      ws.onerror = (error) => {
        clearTimeout(timeout);
        console.error('WebSocket error:', error);
        reject(error);
      };

      ws.onclose = () => {
        setIsConnected(false);
        setStatus('Disconnected');
      };

      wsRef.current = ws;
    });
  };

  const playAudioChunk = async (audioData) => {
    try {
      // Initialize audio context if needed
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        });
      }

      // Decode and play audio
      const audioBuffer = await audioContextRef.current.decodeAudioData(audioData);
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContextRef.current.destination);
      source.start();
      
      // Animate orb while playing
      setOrbPulse(1.3);
      setTimeout(() => setOrbPulse(1), 200);
      
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  };

  const startRecording = async () => {
    try {
      if (!recognitionRef.current) {
        toast.error('Speech recognition not supported in this browser. Please use text input.');
        setShowTextInput(true);
        return;
      }

      // Clear previous transcript (both displayed state and persistent buffer)
      setTranscribedText('');
      finalTranscriptRef.current = '';

      // Mark recording active BEFORE start() so the onend auto-restart guard
      // sees the latest value if the browser bounces recognition immediately.
      isRecordingRef.current = true;

      // Start speech recognition
      recognitionRef.current.start();
      setIsRecording(true);
      setStatus('Listening...');
      
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'start_speaking' }));
      }
      
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = async () => {
    // Flip the ref first so the onend handler does NOT auto-restart after
    // we explicitly stop here.
    isRecordingRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // SpeechRecognition.stop() throws if already stopped — expected.
        if (process.env.NODE_ENV !== 'production') {
          console.debug('SpeechRecognition.stop ignored:', e?.message);
        }
      }
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    setIsRecording(false);
    
    // Wait a moment for final transcript to be captured
    setTimeout(async () => {
      // Prefer the persistent ref (accumulated finals across any pauses/restarts)
      // and merge with any in-flight interim text held in component state.
      const refText = finalTranscriptRef.current?.trim() || '';
      const stateText = transcribedText?.trim() || '';
      const finalTranscript = (
        refText.length >= stateText.length ? refText : stateText
      ).trim();
      
      if (finalTranscript && finalTranscript.length > 0) {
        // Show what we captured if it's long
        if (finalTranscript.length > 100) {
          toast.info(`Captured: "${finalTranscript.substring(0, 50)}..."`, {
            duration: 2000,
            style: {
              background: '#1c1917',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#a1a1aa'
            }
          });
        }
        
        setStatus('SAGE is thinking...');
        await sendToSage(finalTranscript);
      } else {
        toast.error('No speech detected. Please try again or use the text input below.', {
          duration: 4000
        });
        setStatus('SAGE is ready');
        setShowTextInput(true);  // Show text input as fallback
      }
    }, 500);
  };

  const sendToSage = async (input = '') => {
    try {
      const token = localStorage.getItem('sage_token');
      // Re-detect timezone each call so mid-session travel/DST is handled.
      let tzName = null;
      try {
        tzName = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (tzErr) {
        // Same fallback rationale as initializeConnection — backend defaults
        // to UTC when Intl.DateTimeFormat is unavailable. Dev-only log.
        if (process.env.NODE_ENV !== 'production') {
          console.debug('Timezone detection unavailable:', tzErr?.message);
        }
      }

      const response = await axios.post(
        `${API}/coaching/speak?session_id=${sessionId}`,
        { user_input: input, timezone_name: tzName },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000  // 60 second timeout for AI + TTS generation
        }
      );

      const responseText = response.data.text;
      const audioData = response.data.audio;
      const hasAudio = response.data.has_audio;
      
      // Clear displayed text while waiting for audio
      setDisplayedText('');
      
      // Play ElevenLabs audio if available, otherwise fall back to browser TTS
      setStatus('SAGE speaks');
      if (hasAudio && audioData) {
        await playElevenLabsAudio(audioData, responseText);
      } else {
        // For browser TTS, display text immediately
        setDisplayedText(responseText);
        await speakText(responseText);
      }
      
      setUserInput('');
      
    } catch (error) {
      console.error('Failed to get response:', error);
      
      // Clear any partial displayed text
      setDisplayedText('');
      
      // Provide more specific error messages based on the error type
      let errorMessage = 'Something went wrong. ';
      let statusMessage = 'Ready to try again';
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'The request took too long. Try speaking in shorter segments.';
      } else if (error.response?.status === 500) {
        errorMessage = 'SAGE had trouble processing that. Try rephrasing or speaking again.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Session expired. Please refresh the page.';
        statusMessage = 'Session expired';
      } else if (error.response?.status === 404) {
        errorMessage = 'Session not found. Please start a new session.';
        statusMessage = 'Session lost';
      } else if (!navigator.onLine) {
        errorMessage = 'You appear to be offline. Check your connection.';
        statusMessage = 'Offline';
      } else {
        errorMessage = 'Couldn\'t connect to SAGE. Please try again.';
      }
      
      toast.error(errorMessage, {
        duration: 5000,
        style: {
          background: '#451a03',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          color: '#fef3c7'
        }
      });
      setStatus(statusMessage);
      setOrbPulse(1);
    }
  };

  const handleTextSubmit = async (e) => {
    e.preventDefault();
    if (!userInput.trim()) {
      toast.error('Please enter your question');
      return;
    }
    
    setStatus('SAGE is thinking...');
    setOrbPulse(1.2);
    await sendToSage(userInput);
  };

  const endSession = async () => {
    if (!sessionId || isEnding) return;
    
    setIsEnding(true);
    setStatus('Summarizing session notes...');
    
    // Clear any displayed text
    setDisplayedText('');
    
    try {
      const token = localStorage.getItem('sage_token');
      const response = await axios.post(
        `${API}/coaching/end?session_id=${sessionId}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000  // Give enough time for AI summary generation
        }
      );
      
      setSessionSummary(response.data);
      setShowEndSummary(true);
      setIsConnected(false);
      
      if (response.data.todos_created > 0) {
        toast.success(`Session complete! ${response.data.todos_created} action item(s) captured.`);
      } else {
        toast.success('Session complete!');
      }
    } catch (error) {
      console.error('Failed to end session:', error);
      toast.error('Failed to save session notes. Your conversation may not be saved.');
      navigate('/dashboard');
    } finally {
      setIsEnding(false);
    }
  };

  const handleBack = async () => {
    if (sessionId && isConnected) {
      await endSession();
    } else {
      navigate('/dashboard');
    }
  };

  const startVisualization = () => {
    const animate = () => {
      if (!analyserRef.current) return;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // Calculate average volume
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalized = average / 255;
      
      // Pulse the orb based on audio input
      setOrbPulse(1 + normalized * 0.5);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
  };

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />
      
      {/* Session End Summary Modal */}
      {showEndSummary && sessionSummary && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6">
          <div className="bg-stone-950 border border-white/10 rounded-sm max-w-lg w-full p-5 sm:p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => navigate('/dashboard')}
              className="absolute top-3 right-3 sm:top-4 sm:right-4 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <h2 
              className="text-xl sm:text-2xl font-light text-white mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Session Complete
            </h2>
            <p className="text-xs text-zinc-500 mb-4 sm:mb-6">
              {sessionSummary.duration_minutes} minutes
            </p>
            
            <div className="space-y-3 sm:space-y-4">
              <div>
                <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
                  Summary
                </h3>
                <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed">
                  {sessionSummary.summary}
                </p>
              </div>
              
              {sessionSummary.insights?.length > 0 && (
                <div>
                  <h3 className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
                    Key Insights
                  </h3>
                  <ul className="space-y-1">
                    {sessionSummary.insights.map((insight, i) => (
                      <li key={`insight-${i}-${(insight || '').slice(0, 16)}`} className="text-xs sm:text-sm text-zinc-400 flex items-start gap-2">
                        <span className="text-white/50">•</span>
                        {insight}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {sessionSummary.todos_created > 0 && (
                <div className="bg-white/5 rounded-sm p-3 sm:p-4">
                  <p className="text-xs sm:text-sm text-white">
                    ✓ {sessionSummary.todos_created} action item{sessionSummary.todos_created > 1 ? 's' : ''} captured
                  </p>
                  <p className="text-xs text-zinc-500 mt-1">
                    View in your dashboard commitments
                  </p>
                </div>
              )}
            </div>
            
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full frost-button rounded-sm px-6 py-3 mt-4 sm:mt-6 text-sm sm:text-base"
              data-testid="go-to-dashboard-btn"
            >
              Return to Dashboard
            </Button>
          </div>
        </div>
      )}
      
      <div className="relative z-10">
        {/* Session Ending Overlay - blocks all interactions */}
        {isEnding && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 border-2 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-6"></div>
              <h2 
                className="text-2xl font-light text-white mb-2"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Summarizing Session Notes
              </h2>
              <p className="text-sm text-zinc-400">
                SAGE is capturing insights and action items...
              </p>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between">
            <Button
              onClick={handleBack}
              disabled={isEnding || isSpeaking}
              aria-label={isEnding ? 'Ending session, summarizing' : 'End session and return to dashboard'}
              className={`bg-transparent border border-white/20 text-white rounded-sm px-3 sm:px-4 py-2 transition-all flex items-center gap-1 sm:gap-2 text-sm sm:text-base ${
                isEnding || isSpeaking ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/5'
              }`}
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              <span className="hidden sm:inline">{isEnding ? 'Summarizing...' : 'End Session'}</span>
              <span className="sm:hidden">{isEnding ? '...' : 'End'}</span>
            </Button>
            
            <div
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 flex items-center gap-3"
              role="status"
              aria-label={isConnected ? 'Connection status: connected' : 'Connection status: disconnected'}
            >
              <span>{isConnected ? '● Connected' : '○ Disconnected'}</span>
              <ThemeToggle size="sm" />
              <TutorialLaunchButton variant="icon" />
            </div>
          </div>
        </div>
        
        {/* Voice-first coaching — no visible transcript.
            Both SAGE's textual response and the user's live speech-recognition
            captions are intentionally hidden so nothing grows/reflows around
            the mic button while the user is holding it. The audio (SAGE's TTS)
            and the compact "SAGE is speaking…" pulse below cover the entire
            listening experience. If a user needs to replay SAGE's last audio,
            the audio ref is preserved (see stopSpeaking / audio queue). */}
        <div
          className="fixed bottom-0 left-0 right-0 z-30 pointer-events-none"
          aria-live="polite"
          aria-atomic="false"
          role="status"
        >
          {/* Speaking indicator while audio plays (compact, centered, bottom-pinned) */}
          {isSpeaking && (
            <div
              className="pointer-events-auto mx-auto mb-6 sm:mb-8 flex justify-center"
              data-testid="speaking-indicator"
            >
              <div className="bg-stone-900/80 backdrop-blur-sm border border-white/10 rounded-full px-6 py-3">
                <div className="flex items-center gap-3">
                  <Sparkles
                    className="w-4 h-4 text-white/80 animate-pulse"
                    aria-hidden="true"
                  />
                  <div className="flex gap-1" aria-hidden="true">
                    <span className="w-1.5 h-4 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-6 bg-white/80 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-4 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span className="text-sm text-zinc-300">SAGE is speaking…</span>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Session Timer - Fixed bottom left, responsive positioning */}
        <div 
          className="fixed bottom-4 left-4 sm:bottom-8 sm:left-8 bg-stone-900/80 backdrop-blur-sm border border-white/10 rounded-lg px-3 py-2 sm:px-5 sm:py-4 z-20"
          data-testid="session-timer"
        >
          <div className="text-xl sm:text-3xl font-light text-white tracking-wider font-mono">
            {formatTime(elapsedTime)}
          </div>
          <div className="text-[10px] sm:text-xs text-zinc-500 tracking-wide mt-1 text-center">
            {formatDate()}
          </div>
        </div>

        {/* Main Coaching Interface */}
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] sm:min-h-[calc(100vh-100px)] px-4 sm:px-6 py-8 sm:py-0">
          {/* Audio Orb */}
          <div className="relative mb-8 sm:mb-12">
            {/* Outer glow rings */}
            <div 
              className="absolute inset-0 rounded-full bg-white/5 blur-3xl"
              style={{
                transform: `scale(${orbPulse * 2})`,
                transition: 'transform 0.1s ease-out'
              }}
            />
            <div 
              className="absolute inset-0 rounded-full bg-white/10 blur-2xl"
              style={{
                transform: `scale(${orbPulse * 1.5})`,
                transition: 'transform 0.1s ease-out'
              }}
            />
            
            {/* Core orb - smaller on mobile */}
            <div 
              className={`relative w-48 h-48 sm:w-64 sm:h-64 rounded-full flex items-center justify-center ${
                isRecording ? 'bg-white/20' : 'bg-white/10'
              } backdrop-blur-sm border border-white/20 transition-all duration-300`}
              style={{
                transform: `scale(${orbPulse})`,
                boxShadow: isRecording 
                  ? '0 0 80px rgba(255, 255, 255, 0.3), inset 0 0 80px rgba(255, 255, 255, 0.1)'
                  : '0 0 40px rgba(255, 255, 255, 0.1), inset 0 0 40px rgba(255, 255, 255, 0.05)'
              }}
              data-testid="audio-orb"
            >
              {/* Inner pulse */}
              <div 
                className="absolute w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/30 blur-xl animate-pulse"
                style={{
                  animationDuration: isRecording ? '1s' : '3s'
                }}
              />
              
              {/* SAGE's presence — a living particle swarm instead of the
                  previous static Sparkles icon. `state` drives the motion:
                  idle → gentle drift, listening → converges inward while the
                  user speaks, speaking → radiates outward with each word.
                  Occupies the same box as the old icon so surrounding layout
                  is untouched. */}
              <div className="absolute inset-0 z-10 pointer-events-none" data-testid="sage-orb-particles-wrapper">
                <SageParticleOrb
                  state={isSpeaking ? 'speaking' : isRecording ? 'listening' : 'idle'}
                  audioAnalyser={sageAnalyserRef}
                />
              </div>
            </div>
          </div>

          {/* Status Text */}
          <div className="mb-6 sm:mb-8 text-center px-4">
            <h2 
              className="text-2xl sm:text-3xl font-light text-white mb-2"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {status}
            </h2>
            {/* User speech-recognition captions intentionally hidden in
                voice-first mode — see also the removed SAGE response well
                above. Layout stability while the user holds the mic. */}
            {!isGreeting && (
              <p className="text-xs sm:text-sm font-mono uppercase tracking-widest text-zinc-400 mt-2">
                {isSpeaking 
                  ? 'Listen to SAGE\'s wisdom' 
                  : isRecording 
                    ? 'Speak now - I\'m listening' 
                    : showTextInput 
                      ? 'Type your question below'
                      : 'Press and hold to speak or type below'
                }
              </p>
            )}
          </div>

          {/* Control Button + text-input form. Hidden while SAGE is delivering
              her opening greeting so the user is invited to simply listen first. */}
          {!isGreeting && (
            <>
              {/* Control Button - press & hold to record. Keyboard: focus + hold Space. */}
              <Button
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                onKeyDown={(e) => {
                  if ((e.key === ' ' || e.key === 'Enter') && !e.repeat && !isRecording) {
                    e.preventDefault();
                    startRecording();
                  }
                }}
                onKeyUp={(e) => {
                  if ((e.key === ' ' || e.key === 'Enter') && isRecording) {
                    e.preventDefault();
                    stopRecording();
                  }
                }}
                disabled={!isConnected || isEnding || isSpeaking}
                aria-label={isRecording ? 'Recording. Release to send' : 'Press and hold to speak to SAGE'}
                aria-pressed={isRecording}
                className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full transition-all duration-300 touch-none select-none [-webkit-touch-callout:none] [-webkit-user-select:none] ${
                  isRecording 
                    ? 'bg-white text-black scale-110' 
                    : 'bg-white/10 text-white hover:bg-white/20'
                } ${(!isConnected || isEnding || isSpeaking) ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="speak-button"
              >
                {isRecording ? (
                  <MicOff className="w-6 h-6 sm:w-8 sm:h-8" aria-hidden="true" />
                ) : (
                  <Mic className="w-6 h-6 sm:w-8 sm:h-8" aria-hidden="true" />
                )}
              </Button>

              {/* Text Input Alternative */}
              <div className="mt-6 sm:mt-8 w-full max-w-md px-4 sm:px-0">
                {/* Always-visible utility row under the mic: text-input
                    toggle + paperclip. Both are session-scoped controls,
                    so they belong together and should be reachable
                    without first revealing the text input. */}
                <div className="flex items-center justify-center gap-4 mb-4">
                  <button
                    onClick={() => setShowTextInput(!showTextInput)}
                    disabled={isEnding || isSpeaking}
                    aria-expanded={showTextInput}
                    aria-controls="sage-text-input-form"
                    className={`text-xs text-zinc-400 transition-colors ${
                      isEnding || isSpeaking ? 'opacity-50 cursor-not-allowed' : 'hover:text-zinc-200'
                    }`}
                  >
                    {showTextInput ? 'Hide text input' : 'Or type your question'}
                  </button>
                  <span className="text-zinc-700" aria-hidden="true">·</span>
                  <button
                    type="button"
                    onClick={() => setAttachSheetOpen(true)}
                    disabled={!sessionId || isEnding}
                    className={`text-xs text-zinc-400 transition-colors inline-flex items-center gap-1.5 ${
                      !sessionId || isEnding ? 'opacity-50 cursor-not-allowed' : 'hover:text-zinc-200'
                    }`}
                    data-testid="attach-paperclip-utility"
                    aria-label={
                      attachedDocs.length
                        ? `Manage attached documents (${attachedDocs.length} attached)`
                        : 'Attach a reference document'
                    }
                    /* Intentionally stays enabled during isSpeaking — the
                       "Or type your question" toggle disables itself while
                       SAGE speaks (avoids interrupting), but attach is a
                       pre-load operation that doesn't interfere with
                       playback, so keeping it reachable lets the user
                       queue up a document for the moment SAGE finishes. */
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    {attachedDocs.length > 0 ? (
                      <span>
                        {attachedDocs.length} document{attachedDocs.length === 1 ? '' : 's'} attached
                      </span>
                    ) : (
                      <span>Attach a document</span>
                    )}
                  </button>
                </div>

                {/* Attached-doc chips — always visible when there are
                    attachments, even when the text input is hidden. So
                    the user can see and detach what SAGE is holding for
                    this session at a glance. */}
                {attachedDocs.length > 0 && (
                  <div
                    className="flex flex-wrap justify-center gap-2 mb-4"
                    data-testid="attached-docs-chips"
                    aria-label="Reference documents attached to this session"
                  >
                    {attachedDocs.map((d) => (
                      <span
                        key={d.doc_id}
                        className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 rounded-sm px-2.5 py-1 text-xs text-zinc-200"
                        data-testid={`attached-chip-${d.doc_id}`}
                      >
                        <FileText className="w-3 h-3 text-amber-200/70" aria-hidden="true" />
                        <span className="max-w-[10rem] truncate">{d.title}</span>
                        <button
                          type="button"
                          onClick={() => detachDoc(d.doc_id)}
                          className="text-zinc-500 hover:text-white transition-colors"
                          aria-label={`Detach ${d.title}`}
                          data-testid={`attached-chip-detach-${d.doc_id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {showTextInput && (
                  <form
                    id="sage-text-input-form"
                    onSubmit={handleTextSubmit}
                    className="space-y-3 sm:space-y-4"
                    data-testid="text-input-form"
                  >
                    <div className="relative">
                      <label htmlFor="sage-text-input" className="sr-only">
                        Ask SAGE a question
                      </label>
                      <input
                        id="sage-text-input"
                        type="text"
                        value={userInput}
                        onChange={(e) => setUserInput(e.target.value)}
                        placeholder="What's on your mind?"
                        className="w-full bg-stone-950/50 border border-white/10 text-white placeholder:text-zinc-500 px-4 py-3 rounded-sm focus:outline-none focus:border-white/30 transition-all text-sm sm:text-base"
                        data-testid="text-input"
                        disabled={!isConnected || isEnding || isSpeaking}
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={!isConnected || !userInput.trim() || isEnding || isSpeaking}
                      className="w-full bg-white/10 text-white hover:bg-white/20 rounded-sm px-6 py-3 transition-all disabled:opacity-50 text-sm sm:text-base"
                      data-testid="text-submit-button"
                    >
                      Ask SAGE
                    </Button>
                  </form>
                )}
              </div>
            </>
          )}

          {/* Instructions - hidden on mobile for cleaner UI, and during greeting */}
          {!isGreeting && (
            <div className="mt-8 sm:mt-12 text-center max-w-md px-4 hidden sm:block">
              <p className="text-xs text-zinc-400 leading-relaxed">
                {showTextInput 
                  ? "Type your question and SAGE will respond with wisdom based on your profile and knowledge anchors."
                  : "Hold the button and speak. SAGE listens with understanding, drawing from your knowledge anchors to provide personalized guidance."
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Session-scoped reference-docs picker/uploader. Rendered at the
          root so its backdrop covers the whole viewport. */}
      <AttachDocsSheet
        open={attachSheetOpen}
        onClose={() => setAttachSheetOpen(false)}
        sessionId={sessionId}
        attachedIds={attachedDocIds}
        onChange={(nextIds) => setAttachedDocIds(nextIds)}
      />
      <SiteFooter />
    </div>
  );
};

export default LiveCoaching;
